import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { FilterContainer } from 'components/Filter/Filter.styled';
import { TireFilters } from 'domains/tires/Tire.types';
import { Box, CenterFlex, CenteredSpin, Flex, MarginBox, Text } from 'UI';
import FilterFacetItem from './components/FilterFacetItem';
import { UsePrice } from './Filter.shared';
import {
  FILTER_FEATURES,
  FILTER_FUEL_EFFICIENCY,
  FILTER_NOISE_LEVEL,
  FILTER_PRODUCT_RANGE,
  FILTER_WET_GRIP,
  Filters,
  FiltersRange,
  FilterType,
  getFilterItemDisplayOrder,
  getTextFacets,
  isValidPosition,
  RANGE_FILTER_TYPE,
  RangeFacetLocal,
  sortFacets,
  TextFacetLocal,
} from './Filter.types';

interface FilterProps {
  filters: Filters | TireFilters;
  textFilters: TextFacetLocal[] | undefined;
  rangeFilters: RangeFacetLocal[] | undefined;
  setTextFilters?: (id: string, item: string) => void;
  setRangeFilters?: (id: string, range?: FiltersRange) => void;
  resetFilters: () => void;
  loading: boolean;
  /** Facet ids that should show a loading spinner independently of the global `loading` flag. */
  loadingFacetIds?: string[];
  facetsMap: Map<string, { label: string; ids: string[] }>;
  usePrice?: UsePrice;
  requiredMin?: number;
  type?: FilterType;
  inline?: boolean;
  reversed?: boolean;
  displayWhenEmpty?: boolean;
}

const collapsedOnStart = [
  FILTER_FUEL_EFFICIENCY,
  FILTER_FEATURES,
  FILTER_PRODUCT_RANGE,
  FILTER_WET_GRIP,
  FILTER_NOISE_LEVEL,
];

const Filter = ({
  filters,
  textFilters = [],
  rangeFilters = [],
  setTextFilters,
  setRangeFilters,
  resetFilters,
  loading = false,
  loadingFacetIds = [],
  facetsMap,
  usePrice,
  requiredMin = 1,
  type,
  inline,
  reversed,
  displayWhenEmpty = true,
}: FilterProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const facets: (TextFacetLocal | RangeFacetLocal)[] = useMemo(
    () =>
      [...getTextFacets(textFilters, requiredMin), ...rangeFilters]
        .map((fct) => {
          return {
            ...fct,
            order: getFilterItemDisplayOrder(type, fct),
          };
        })
        .sort(sortFacets),
    [textFilters, requiredMin, rangeFilters, type],
  );
  const defaultActiveKeys = facets.map((facet) => facet.id).filter((k) => !collapsedOnStart.includes(k));

  const handleResetFilters = () => {
    resetFilters();
    if (usePrice) {
      usePrice.setShowPriceless(true);
    }
  };

  const setTextFilterDebounceDelay = 200;
  const setTextFilter = useDebouncedCallback((id: string, item: string) => {
    if (setTextFilters) {
      setTextFilters(id, item);
    }
  }, setTextFilterDebounceDelay);

  const getTextChecked = (id: string, item: string) => (filters.textFilters.get(id) ?? []).includes(item);

  const applyRangeFilter = useCallback(
    (id: string, range?: { min: number; max: number }) => {
      if (setRangeFilters) {
        setRangeFilters(id, range);
      }
    },
    [setRangeFilters],
  );

  const setRangeFilterDebounced = useDebouncedCallback(applyRangeFilter, 500);

  const getRangeValues = (id: string): [number, number] | undefined => {
    const idRangeItem = filters.rangeFilters.get(id);
    return idRangeItem && [idRangeItem.min, idRangeItem.max];
  };

  const renderFacets = () => {
    const facetElements = facets.map((facet) => {
      const facetLabel = facetsMap.get(facet.id)?.label ?? facet.id;
      const rangeFacet = facet as RangeFacetLocal;
      const filterValues: [number, number] =
        facet.type === RANGE_FILTER_TYPE
          ? getRangeValues(facet.id) ?? [rangeFacet.min ?? 0, rangeFacet.max ?? 0]
          : [0, 0];

      return (
        <FilterFacetItem
          key={facet.id}
          facet={facet}
          facetLabel={facetLabel}
          loading={loading || loadingFacetIds.includes(facet.id)}
          inline={inline ?? false}
          defaultActiveKeys={defaultActiveKeys}
          type={type}
          t={t}
          isValidPosition={isValidPosition}
          getTextChecked={getTextChecked}
          setTextFilter={setTextFilter}
          filterValues={filterValues}
          setRangeFilterImmediate={applyRangeFilter}
          setRangeFilterDebounced={setRangeFilterDebounced}
          usePrice={usePrice}
          open={open}
          setOpen={setOpen}
        />
      );
    });

    if (reversed) {
      facetElements.reverse();
    }
    return facetElements;
  };

  if (facets.length === 0 && !displayWhenEmpty) {
    return <></>;
  }

  const facetElements = renderFacets();

  return (
    <>
      {inline ? (
        <Flex direction={'row-reverse'} gap={10}>
          {facetElements}
        </Flex>
      ) : (
        <FilterContainer>
          <Box width={'100%'}>
            <MarginBox ml={15} mt={-15} mr={10}>
              <Flex justify={'space-between'} align={'center'}>
                <Text type="heading4">{t('common.filter.filter_by', 'Filter by')}</Text>
                <Box>
                  <Flex>
                    <Text type="base" cursor={'pointer'} onClick={handleResetFilters} hoverUnderLine>
                      {t('common.filter.action.clear_filters', 'Clear filters')}
                    </Text>
                  </Flex>
                </Box>
              </Flex>
            </MarginBox>
            {facets.length === 0 && loading && (
              <MarginBox my={10}>
                <CenterFlex>
                  <CenteredSpin />
                </CenterFlex>
              </MarginBox>
            )}
            {facets.length === 0 && !loading && (
              <MarginBox my={10}>
                <CenterFlex>
                  <Text type="base">{t('common.filter.no_filter', 'No filter available')}</Text>
                </CenterFlex>
              </MarginBox>
            )}
            {facetElements}
          </Box>
        </FilterContainer>
      )}
    </>
  );
};

export default Filter;
